import _extends from "@babel/runtime/helpers/extends";
import React from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Card, Dropdown, Grid, Icon, Text } from "~/components-shared";
import { AuthoritiesEnum, CRUDActionEnum, TrendReportTypeEnum } from "~/types/enums";
import moment from "moment";
import { usePostHog } from "posthog-js/react";
import styled, { useTheme } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { resolveMonthDays } from "~/lib/helpers";
import trendReportsActionCreators from "~/store/trendReports/actions";
import trendReportSelectors from "~/store/trendReports/selectors";
import { useQueryState } from "~/hooks/common";
import useAbacExt from "~/hooks/abac";
const StyledGrid = styled(Grid)`
  border-radius: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.shape.borderRadius.primary;
}};
`;
const ProductTrendsInfoCard = _ref2 => {
  let {
    ...rest
  } = _ref2;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const historyReports = useSelector(trendReportSelectors.getHistoryReports(TrendReportTypeEnum.PRODUCT));
  const [queryParams, setQueryParams] = useQueryState();
  const reportId = queryParams.reportId || historyReports[0]?.id;
  const report = historyReports.find(r => r.id === reportId);
  const scheduledDate = moment.utc("5:00pm", "hh:mma").weekday(4).local();
  const deliveryDate = moment().valueOf() > scheduledDate.valueOf() ? scheduledDate.add("7", "days") : scheduledDate; // Friday 10:00am
  const historyError = useSelector(trendReportSelectors.getHistoryError(TrendReportTypeEnum.PRODUCT));
  const reportError = useSelector(trendReportSelectors.getErrorByReport(reportId));
  const error = reportError || historyError;
  const {
    userHas
  } = useAbacExt();
  const posthog = usePostHog();
  const handleClickReport = id => {
    setQueryParams({
      reportId: id
    });
    posthog.capture(`Topic Report – Select`, {
      reportId: id
    });
  };
  const handleDelete = () => {
    dispatch(trendReportsActionCreators.actionChanged(CRUDActionEnum.DELETE, reportId));
    posthog.capture(`Topic Report – Delete`, {
      reportId
    });
  };
  const gridTemplate = ["auto", "auto", "auto"];
  if (userHas([AuthoritiesEnum.EXPERIMENTAL])) {
    gridTemplate.push("2.7rem");
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 6
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Card, _extends({
    color: "transparent",
    border: true
  }, rest), /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 8,
    gap: 0,
    template: gridTemplate.join(" "),
    s: {
      pl: 6,
      pr: 4,
      py: 3
    },
    align: "stretch"
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2.5
    }
  }, t("ProductTrendsInfoCard.reportType")), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "bold",
    fontSize: 4
  }, t("ProductTrendsInfoCard.weeklyProductTrendsReport"))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 1
    }
  }, t("ProductTrendsInfoCard.period")), report ? /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    customHandler: (toggle, isOpen) => /*#__PURE__*/React.createElement(Button, {
      color: "translucent",
      s: {
        py: 1.5,
        pl: 4,
        pr: 2,
        ml: -3
      },
      onClick: toggle,
      outline: false
    }, /*#__PURE__*/React.createElement(Text, {
      display: "block",
      weight: "bold",
      fontSize: 4
    }, resolveMonthDays(report.dateFrom, report.dateTo, true), /*#__PURE__*/React.createElement(Icon, {
      src: "chevron",
      scale: 5,
      s: {
        ml: 1
      },
      rotate: isOpen ? 0 : 180
    })))
  }, _ref3 => {
    let {
      isOpen,
      onItemClick
    } = _ref3;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen,
      width: "200px"
    }, historyReports.map((item, index) => /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      key: item.id,
      onClick: () => {
        handleClickReport(index === 0 ? undefined : item.id);
        onItemClick();
      },
      selected: item.id === reportId
    }, /*#__PURE__*/React.createElement(Grid, {
      cols: 2,
      template: "1fr auto",
      fullWidth: true
    }, /*#__PURE__*/React.createElement(Text, {
      weight: "semiBold"
    }, resolveMonthDays(item.dateFrom, item.dateTo, true))))));
  }) : /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "bold",
    fontSize: 4,
    s: {
      mt: 2.5
    }
  }, "Not delivered")), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2.5
    }
  }, t("ProductTrendsInfoCard.nextDeliveryDate")), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "bold",
    fontSize: 4
  }, deliveryDate.format("ddd, MMM Do [at] h:mma"))), userHas([AuthoritiesEnum.EXPERIMENTAL]) && /*#__PURE__*/React.createElement(Grid.Cell, {
    align: "center"
  }, /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    display: "inline-block",
    handlerProps: {
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "dots-vertical",
      color: colors.text.primary,
      display: "block"
    }),
    fullWidth: true
  }, _ref4 => {
    let {
      isOpen,
      onItemClick
    } = _ref4;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleDelete();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "bin",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "danger"
    }), t("common.actions.delete"), /*#__PURE__*/React.createElement(Icon, {
      src: "encrypted",
      color: "primary",
      s: {
        ml: 1
      }
    })));
  })))));
};
export default ProductTrendsInfoCard;